"use client"

import { useQuery } from "@tanstack/react-query"

import { apiClient } from "@/lib/api/client"
import { apiUrl } from "@/lib/api/url"
import { raceKeys } from "@/lib/apps/tsp/keys/race"

import type { SeasonRace } from "@/nango/types/apps.tsp/dashboard/SeasonRace"
import type {
  CircuitSummary,
  Race,
  RaceWithRuns,
} from "@/nango/types/apps.tsp/race/Race"

export function useTspRaces(
  filters?: {
    discipline?: string
    circuit?: string
    gender?: string
    year?: string
    athlete?: number
  },
  enabled: boolean = true,
) {
  const params = new URLSearchParams()
  if (filters?.discipline) params.set("discipline", filters.discipline)
  if (filters?.circuit) params.set("circuit", filters.circuit)
  if (filters?.gender) params.set("gender", filters.gender)
  if (filters?.year) params.set("year", filters.year)
  if (filters?.athlete) params.set("athlete", String(filters.athlete))
  const query = params.toString() ? `?${params.toString()}` : ""
  return useQuery({
    queryKey: raceKeys.list(filters),
    queryFn: () =>
      apiClient<Race[]>({
        url: apiUrl(`api/tsp/tsp_race/${query}`),
        method: "GET",
      }),
    enabled,
  })
}

export function useTspRace(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: raceKeys.detail(id),
    queryFn: () =>
      apiClient<Race>({
        url: apiUrl(`api/tsp/tsp_race/${id}/`),
        method: "GET",
      }),
    enabled,
  })
}

export function useTspRaceCircuits(enabled: boolean = true) {
  return useQuery({
    queryKey: raceKeys.circuits(),
    queryFn: () =>
      apiClient<CircuitSummary[]>({
        url: apiUrl("api/tsp/tsp_race/circuits/"),
        method: "GET",
      }),
    enabled,
  })
}

export function useTspRaceWithRuns(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: raceKeys.withRuns(id),
    queryFn: () =>
      apiClient<RaceWithRuns>({
        url: apiUrl(`api/tsp/tsp_race/${id}/with_runs/`),
        method: "GET",
      }),
    enabled,
  })
}

export function useTspJudgeAnalysis(
  filters: { circuit?: string; gender?: string; year?: string },
  enabled: boolean = true,
) {
  const params = new URLSearchParams()
  if (filters.circuit) params.set("circuit", filters.circuit)
  if (filters.gender) params.set("gender", filters.gender)
  if (filters.year) params.set("year", filters.year)
  return useQuery({
    queryKey: raceKeys.judgeAnalysis(filters),
    queryFn: () =>
      apiClient<Record<string, unknown>>({
        url: apiUrl(`api/tsp/tsp_judge_analysis/?${params.toString()}`),
        method: "GET",
      }),
    enabled,
  })
}

export function useTspCircuitMap(
  circuit: string,
  year?: string,
  enabled: boolean = true,
) {
  const params = new URLSearchParams({ circuit })
  if (year) params.set("year", year)
  return useQuery({
    queryKey: ["tsp", "race", "map", circuit, year ?? null],
    queryFn: () =>
      apiClient<SeasonRace[]>({
        url: apiUrl(`api/tsp/tsp_race/map/?${params.toString()}`),
        method: "GET",
      }),
    enabled,
  })
}
